import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Star } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { CtaButton, Reveal, SectionHeading, GlowOrb, BrandMark } from "@/components/landing/primitives";
import { api } from "@/lib/api";

const TIERS = [
  { name: "Free", price: "0€", perks: ["3 analyses offertes", "Red flags & rizz score", "Diagnostic instantané"] },
  { name: "Basic", price: "4,90€", oldPrice: "9,90€", badge: "🚀 Lancement", launch: true, perks: ["Analyses illimitées", "1 relation suivie", "Réponses sur-mesure"] },
  { name: "Pro", price: "14,99€", badge: "Populaire", featured: true, perks: ["Relations illimitées", "Suivi WhatsApp 24/7", "Alertes de dégradation"] },
  { name: "Docteur", price: "29,99€", perks: ["Tout Pro, +", "Consultations vocales IA", "Plans d'action & check-ins"] },
];

export function TestimonialsSection() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews/public")
      .then((r) => setReviews(r.data.reviews || []))
      .catch(() => {});
  }, []);

  if (!reviews.length) return null;

  return (
    <section id="avis" className="relative py-20 md:py-28">
      <GlowOrb className="w-[420px] h-[420px] top-0 right-0" color="#EC4899" opacity={0.12} />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 relative">
        <Reveal>
          <SectionHeading eyebrow="Avis vérifiés" title="Ce qu'en disent nos utilisateurs" sub="De vrais retours, laissés depuis l'app." center />
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials-grid">
          {reviews.map((r, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <div className="h-full rounded-3xl bg-[#0a0a0a] border border-white/10 p-6">
                <div className="flex gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={15} className={r.rating >= n ? "text-amber-400 fill-amber-400" : "text-zinc-700"} />
                  ))}
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed">“{r.testimonial}”</p>
                <p className="mt-4 text-xs font-semibold text-pink-400">{r.name}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PricingPreview() {
  return (
    <section id="pricing" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <Reveal>
          <SectionHeading eyebrow="Tarifs" title="Choisis ton niveau de soin" sub="Commence gratuitement. Offre de lancement : Premium à 4,90€/mois 🚀" center />
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className={`relative h-full rounded-3xl p-[1px] ${t.featured ? "bg-gradient-to-b from-pink-500 to-purple-600 md:scale-105" : t.launch ? "bg-gradient-to-b from-amber-400 to-rose-500" : ""}`}>
                <div className={`h-full rounded-3xl p-6 border ${t.featured ? "bg-[#0d0a10] border-transparent shadow-[0_0_40px_rgba(236,72,153,0.15)]" : t.launch ? "bg-[#100a08] border-transparent shadow-[0_0_40px_rgba(245,158,11,0.18)]" : "bg-[#0a0a0a] border-white/10"}`}>
                  {t.badge && (
                    <span className={`inline-block text-[10px] font-bold tracking-widest rounded-full px-3 py-1 mb-3 ${t.launch ? "text-amber-300 bg-amber-500/10 border border-amber-500/30" : "text-pink-400 bg-pink-500/10 border border-pink-500/20"}`}>{t.badge}</span>
                  )}
                  <p className="font-semibold text-white">{t.name}</p>
                  <p className="mt-2">
                    {t.oldPrice && <span className="font-display text-xl font-bold text-zinc-500 line-through mr-2">{t.oldPrice}</span>}
                    <span className="font-display text-4xl font-black text-white">{t.price}</span><span className="text-zinc-500 text-sm">/mois</span>
                  </p>
                  <ul className="mt-5 space-y-2">
                    {t.perks.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm text-zinc-300"><Check size={14} className="text-pink-400 shrink-0" /> {p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-8 text-center">
            <CtaButton testid="pricing-preview-cta" to="/pricing" secondary>
              Voir tous les détails <ArrowRight size={16} />
            </CtaButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export const FAQ_ITEMS = [
  { q: "Mes conversations sont-elles privées ?", a: "Oui. Elles sont chiffrées, jamais partagées et jamais utilisées pour entraîner l'IA." },
  { q: "Ça marche avec quelles apps ?", a: "WhatsApp (export ou suivi 24/7), Tinder, Hinge, Bumble et Instagram (screenshots). Tu peux aussi coller le texte." },
  { q: "C'est quoi le suivi WhatsApp 24/7 ?", a: "Avec l'offre Pro, tu connectes WhatsApp et DocLove veille sur ta relation en continu : health score qui évolue, tendances et alertes dès qu'une dégradation ou un red flag apparaît. En lecture seule, jamais d'envoi de message." },
  { q: "Les consultations vocales, comment ça marche ?", a: "Avec l'offre Docteur, tu parles à ton coach IA à voix haute (ou par écrit) et il te répond en vocal, avec le contexte de tes relations. Il peut aussi te bâtir un plan d'action sur plusieurs semaines avec des check-ins." },
  { q: "C'est fiable comme analyse ?", a: "GPT-4o couplé à des prompts spécialisés dating FR. ~92% de précision sur les red flags standards." },
  { q: "Je peux me désabonner ?", a: "Oui, en 1 clic, à tout moment. Aucun engagement." },
  { q: "Ça remplace un vrai psy ?", a: "Non. DocLove est un outil d'analyse, pas un thérapeute humain. Consulte un pro si ça ne va pas." },
  { q: "Vous vendez mes données ?", a: "Jamais. Notre seul revenu, c'est l'abonnement." },
  { q: "Il faut installer une app ?", a: "Non, DocLove est une PWA. Ajoute-la à ton écran d'accueil en 1 tap." },
  { q: "Ça marche sur iPhone ?", a: "Oui, à 100%. Aucun App Store nécessaire." },
];

export function FAQSection() {
  return (
    <section id="faq" className="relative py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-5 sm:px-6">
        <Reveal>
          <SectionHeading eyebrow="FAQ" title="Questions fréquentes" center />
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-item-${i}`} className="text-left text-white hover:text-pink-400 hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-pink-600/20 via-purple-600/10 to-transparent" />
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay bg-cover bg-center"
        style={{ backgroundImage: "url(https://images.pexels.com/photos/7351139/pexels-photo-7351139.jpeg)" }}
        aria-hidden
      />
      <GlowOrb className="w-[500px] h-[500px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" color="#EC4899" opacity={0.2} />
      <div className="relative max-w-3xl mx-auto px-5 sm:px-6 text-center">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight text-white">Décode ta conv en 30 secondes.</h2>
          <p className="mt-4 text-zinc-300">3 analyses gratuites. Aucune carte requise.</p>
          <div className="mt-8 flex justify-center">
            <CtaButton testid="final-cta" event="lead">Commencer maintenant <ArrowRight size={18} /></CtaButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function LandingFooter() {
  return (
    <footer className="border-t border-white/10 py-12">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-2">
            <BrandMark size={20} />
            <span className="font-display text-lg font-bold">DocLove</span>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-500">
            <Link to="/pricing" className="hover:text-white transition-colors" data-testid="footer-pricing">Tarifs</Link>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            <Link to="/confidentialite" className="hover:text-white transition-colors" data-testid="footer-privacy">Confidentialité</Link>
            <Link to="/cgu" className="hover:text-white transition-colors" data-testid="footer-cgu">CGU</Link>
            <Link to="/mentions-legales" className="hover:text-white transition-colors" data-testid="footer-legal">Mentions légales</Link>
            <a href="https://www.tiktok.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">TikTok</a>
            <a href="https://www.instagram.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Instagram</a>
          </nav>
        </div>
        <p className="mt-8 text-xs text-zinc-600">© 2026 DocLove · Made with 💜 in France</p>
      </div>
    </footer>
  );
}
